import { LAB_FIXTURE } from 'views/playground/features/data-lab/constants/lab-fixtures';
import {
  CartesianData,
  CategoryPoint,
  NamedSeries,
  NestedCategoryData,
  ScatterSeriesData,
  TimeSeriesData,
} from 'views/playground/features/data-lab/types/chart-series.type';
import { CageWeek } from 'views/playground/features/data-lab/types/cage.type';
import { HealthStatus } from 'views/playground/features/data-lab/types/health-status.type';
import { healthStatusFor, MORTALITY_THRESHOLDS } from 'views/playground/features/data-lab/utils/health-status';

const WEEK_COUNT = 26;
const LATEST_WEEK = WEEK_COUNT;
const OTHER_ID = 'other';

// Build the per-cage index once because the screen derives many chart series
// from these week records.
const buildCageWeekIndex = (): Map<string, CageWeek[]> => {
  const index = new Map<string, CageWeek[]>();
  LAB_FIXTURE.cageWeeks.forEach((cageWeek) => {
    const weeks = index.get(cageWeek.cageId);
    if (weeks) weeks.push(cageWeek);
    else index.set(cageWeek.cageId, [cageWeek]);
  });
  index.forEach((weeks) => weeks.sort((a, b) => a.week - b.week));
  return index;
};

const cageWeekIndex = buildCageWeekIndex();

const cageWeeksFor = (cageId: string): CageWeek[] => cageWeekIndex.get(cageId) ?? [];

const cageWeekAt = (cageId: string, week: number) => cageWeeksFor(cageId).find((cageWeek) => cageWeek.week === week);

const cagesForSite = (siteId: string) => LAB_FIXTURE.cages.filter((cage) => cage.siteId === siteId);

const siteBiomassAt = (siteId: string, week: number): number =>
  cagesForSite(siteId).reduce((total, cage) => total + (cageWeekAt(cage.id, week)?.biomassKg ?? 0), 0);

// Groups every point beyond the first `keep` into an "Other" bucket.
// `otherLabel` comes from the caller so the screen can localize it.
const groupTopWithOther = (points: CategoryPoint[], keep: number, otherLabel: string): CategoryPoint[] => {
  const sorted = [...points].sort((a, b) => b.value - a.value);
  const kept = sorted.slice(0, keep);
  const rest = sorted.slice(keep);
  if (rest.length === 0) return kept;

  const otherValue = rest.reduce((total, point) => total + point.value, 0);
  return [...kept, { id: OTHER_ID, label: otherLabel, value: otherValue }];
};

const siteTotalsAtWeek = (week: number): CategoryPoint[] =>
  LAB_FIXTURE.sites.map((site) => ({ id: site.id, label: site.name, value: Math.round(siteBiomassAt(site.id, week)) }));

// Reconstructs population per week for one cage. CageWeek only stores that week's
// mortality count, so the running population is derived from it rather than stored
// directly. Approximate: the initial population is back-solved from week 1's
// biomass and average weight.
const populationSeriesFor = (cageId: string): number[] => {
  const weeks = cageWeeksFor(cageId);
  if (weeks.length === 0) return [];

  let population = Math.round(weeks[0].biomassKg / (weeks[0].averageWeightG / 1000)) + weeks[0].mortality;

  return weeks.map((cageWeek) => {
    population -= cageWeek.mortality;
    return population;
  });
};

export const deriveTrendLineData = (): TimeSeriesData => {
  const site = LAB_FIXTURE.sites[0];
  const cageIds = site.cageIds.slice(0, 3);
  const points = Array.from({ length: WEEK_COUNT }, (_unused, index) => `W${index + 1}`);

  const series: NamedSeries[] = cageIds.map((cageId) => {
    const cage = LAB_FIXTURE.cages.find((candidate) => candidate.id === cageId);
    return {
      id: cageId,
      label: cage?.name ?? cageId,
      data: cageWeeksFor(cageId).map((cageWeek) => cageWeek.biomassKg),
    };
  });

  return { points, series };
};

export const deriveCategoryBarData = (): CartesianData => {
  const categories: CategoryPoint[] = LAB_FIXTURE.sites.map((site) => ({
    id: site.id,
    label: site.name,
    value: Math.round(siteBiomassAt(site.id, LATEST_WEEK)),
  }));

  const feedTotals = LAB_FIXTURE.sites.map((site) =>
    cagesForSite(site.id).reduce((total, cage) => total + (cageWeekAt(cage.id, LATEST_WEEK)?.feedKg ?? 0), 0)
  );

  const series: NamedSeries[] = [
    { id: 'biomass', label: 'Biomass (kg)', data: categories.map((category) => category.value) },
    { id: 'feed', label: 'Feed (kg)', data: feedTotals.map((value) => Math.round(value)) },
  ];

  return { categories, series };
};

export const deriveSeverityBarData = (): CategoryPoint[] => {
  const points = LAB_FIXTURE.cages.map((cage) => {
    const weeks = cageWeeksFor(cage.id);
    const initialPopulation =
      weeks.length > 0 ? Math.round(weeks[0].biomassKg / (weeks[0].averageWeightG / 1000)) + weeks[0].mortality : 0;
    const cumulativeMortality = weeks.reduce((total, cageWeek) => total + cageWeek.mortality, 0);
    const mortalityRate = initialPopulation > 0 ? (cumulativeMortality / initialPopulation) * 100 : 0;

    return { id: cage.id, label: `${cage.siteId} ${cage.name}`, value: Math.round(mortalityRate * 10) / 10 };
  });

  return points.sort((a, b) => b.value - a.value).slice(0, 8);
};

export const deriveDistributionDonutData = (otherLabel: string): CategoryPoint[] =>
  groupTopWithOther(siteTotalsAtWeek(LATEST_WEEK), 4, otherLabel);

export const deriveNestedDonutData = (): NestedCategoryData => {
  const totals = siteTotalsAtWeek(LATEST_WEEK).sort((a, b) => b.value - a.value);
  const half = Math.ceil(totals.length / 2);
  const high = totals.slice(0, half);
  const low = totals.slice(half);

  const sum = (points: CategoryPoint[]) => points.reduce((total, point) => total + point.value, 0);

  const outer: CategoryPoint[] = [
    { id: 'high-biomass', label: 'Higher biomass sites', value: sum(high) },
    { id: 'low-biomass', label: 'Lower biomass sites', value: sum(low) },
  ];

  const inner: CategoryPoint[] = [...high, ...low];

  return { outer, inner };
};

export const deriveCorrelationScatterData = (): ScatterSeriesData[] =>
  LAB_FIXTURE.sites.slice(0, 5).map((site) => ({
    id: site.id,
    label: site.name,
    points: cagesForSite(site.id).map((cage) => {
      const weeks = cageWeeksFor(cage.id);
      const lastWeek = weeks[weeks.length - 1];
      const cumulativeFeed = weeks.reduce((total, cageWeek) => total + cageWeek.feedKg, 0);
      const cumulativeGain = lastWeek ? lastWeek.biomassKg - weeks[0].biomassKg : 0;
      const fcr = cumulativeGain > 0 ? cumulativeFeed / Math.max(cumulativeGain, 1) : 0;

      return {
        id: cage.id,
        x: lastWeek?.averageWeightG ?? 0,
        y: Math.round(fcr * 100) / 100,
      };
    }),
  }));

export const deriveConditionRadarData = (): { metrics: string[]; series: NamedSeries[] } => {
  const metrics = ['Biomass', 'Growth rate', 'Feed efficiency', 'Water temp', 'Survival'];

  const scoreForCage = (cageId: string): number[] => {
    const weeks = cageWeeksFor(cageId);
    const first = weeks[0];
    const last = weeks[weeks.length - 1];
    const initialPopulation = first ? Math.round(first.biomassKg / (first.averageWeightG / 1000)) + first.mortality : 0;
    const cumulativeMortality = weeks.reduce((total, cageWeek) => total + cageWeek.mortality, 0);
    const survivalRate = initialPopulation > 0 ? 100 - (cumulativeMortality / initialPopulation) * 100 : 0;
    const growthRate =
      first && last ? ((Math.log(last.averageWeightG) - Math.log(first.averageWeightG)) / (weeks.length * 7)) * 100 * 10 : 0;
    const cumulativeFeed = weeks.reduce((total, cageWeek) => total + cageWeek.feedKg, 0);
    const cumulativeGain = last && first ? last.biomassKg - first.biomassKg : 0;
    const fcr = cumulativeGain > 0 ? cumulativeFeed / cumulativeGain : 2;
    const feedEfficiency = Math.max(0, 100 - (fcr - 1) * 100);
    const averageTemp = weeks.reduce((total, cageWeek) => total + cageWeek.temperatureC, 0) / (weeks.length || 1);
    const tempScore = Math.max(0, 100 - Math.abs(averageTemp - 10) * 8);
    const biomassScore = last ? Math.min(100, (last.biomassKg / 60000) * 100) : 0;

    return [biomassScore, Math.min(100, Math.max(0, growthRate)), feedEfficiency, tempScore, survivalRate].map(
      (value) => Math.round(value * 10) / 10
    );
  };

  const anomalousCageId = 'SITE-B-C2';
  const typicalCageId = 'SITE-A-C1';

  const series: NamedSeries[] = [
    { id: typicalCageId, label: `${typicalCageId} (typical)`, data: scoreForCage(typicalCageId) },
    { id: anomalousCageId, label: `${anomalousCageId} (mortality event)`, data: scoreForCage(anomalousCageId) },
  ];

  return { metrics, series };
};

export const deriveUtilizationGaugeData = (cageId = 'SITE-A-C1'): { value: number; max: number } => {
  const cage = LAB_FIXTURE.cages.find((candidate) => candidate.id === cageId);
  const lastWeek = cageWeekAt(cageId, LATEST_WEEK);
  if (!cage || !lastWeek) return { value: 0, max: 100 };

  const utilizationPercent = (lastWeek.biomassKg / cage.capacityKg) * 100;
  return { value: Math.round(Math.min(utilizationPercent, 100) * 10) / 10, max: 100 };
};

export const deriveMiniSparklineData = (cageId = 'SITE-A-C1'): number[] => cageWeeksFor(cageId).map((cageWeek) => cageWeek.biomassKg);

// --- Overview dashboard -------------------------------------------------
//
// The Overview's cross-filter is site-scoped: the donut and bar are selectors,
// and the other data reads the same `selectedSite` id. Fixture data carries no
// feed price, so `feedCostPerKg`
// assumes a fixed feed price to turn the already-derived FCR into a cost; every
// other KPI is a direct rollup of `CageWeek` fields.
const RECENT_WEEK_COUNT = 12;
const FEED_PRICE_PER_KG = 1.85;

const cumulativeThroughWeek = (cages: { id: string }[], week: number) => {
  let feed = 0;
  let gain = 0;
  let mortalityCount = 0;
  let initialPopulation = 0;
  let biomass = 0;

  cages.forEach((cage) => {
    const weeks = cageWeeksFor(cage.id).slice(0, week);
    const first = weeks[0];
    const last = weeks[weeks.length - 1];
    if (!first || !last) return;

    feed += weeks.reduce((total, cageWeek) => total + cageWeek.feedKg, 0);
    gain += last.biomassKg - first.biomassKg;
    mortalityCount += weeks.reduce((total, cageWeek) => total + cageWeek.mortality, 0);
    initialPopulation += Math.round(first.biomassKg / (first.averageWeightG / 1000)) + first.mortality;
    biomass += last.biomassKg;
  });

  return { feed, gain, mortalityCount, initialPopulation, biomass };
};

export interface OverviewKpis {
  isEmpty: boolean;
  biomassKg: number;
  biomassTrend: number[];
  feedConversionRatio: number;
  feedConversionTrend: number[];
  mortalityRatePercent: number;
  mortalityTrend: number[];
  feedCostPerKg: number;
  feedCostTrend: number[];
}

const EMPTY_OVERVIEW_KPIS: OverviewKpis = {
  isEmpty: true,
  biomassKg: 0,
  biomassTrend: [],
  feedConversionRatio: 0,
  feedConversionTrend: [],
  mortalityRatePercent: 0,
  mortalityTrend: [],
  feedCostPerKg: 0,
  feedCostTrend: [],
};

// When `siteId` does not match a real site (the donut's "Other" bucket has no
// backing cages),
// this returns a sentinel the KPI row can detect and render its own empty state
// instead of every field silently reducing to a fabricated zero.
export const deriveOverviewKpis = (siteId?: string | null): OverviewKpis => {
  const site = siteId ? LAB_FIXTURE.sites.find((candidate) => candidate.id === siteId) : undefined;
  if (siteId && !site) return EMPTY_OVERVIEW_KPIS;

  const cages = site ? cagesForSite(site.id) : LAB_FIXTURE.cages;
  const startWeek = Math.max(1, WEEK_COUNT - RECENT_WEEK_COUNT + 1);

  const biomassTrend: number[] = [];
  const feedConversionTrend: number[] = [];
  const mortalityTrend: number[] = [];
  const feedCostTrend: number[] = [];

  for (let week = startWeek; week <= WEEK_COUNT; week += 1) {
    const { feed, gain, mortalityCount, initialPopulation, biomass } = cumulativeThroughWeek(cages, week);
    const feedConversionRatio = gain > 0 ? feed / gain : 0;
    const mortalityRatePercent = initialPopulation > 0 ? (mortalityCount / initialPopulation) * 100 : 0;

    biomassTrend.push(Math.round(biomass));
    feedConversionTrend.push(Math.round(feedConversionRatio * 100) / 100);
    mortalityTrend.push(Math.round(mortalityRatePercent * 10) / 10);
    feedCostTrend.push(Math.round(feedConversionRatio * FEED_PRICE_PER_KG * 100) / 100);
  }

  return {
    isEmpty: false,
    biomassKg: biomassTrend[biomassTrend.length - 1] ?? 0,
    biomassTrend,
    feedConversionRatio: feedConversionTrend[feedConversionTrend.length - 1] ?? 0,
    feedConversionTrend,
    mortalityRatePercent: mortalityTrend[mortalityTrend.length - 1] ?? 0,
    mortalityTrend,
    feedCostPerKg: feedCostTrend[feedCostTrend.length - 1] ?? 0,
    feedCostTrend,
  };
};

// Aggregate biomass over all 26 weeks, scoped to one site when `siteId` is set.
// Returns an empty series when `siteId` matches no real site (the donut's
// "Other" bucket, for instance), so the Overview can show a real empty state
// rather than a flat zero line. `seriesLabel` is threaded from the caller
// rather than hardcoded, since the caller already resolves the site's display
// name for the filter chip.
export const deriveOverviewTrendData = (siteId: string | null | undefined, seriesLabel: string): TimeSeriesData => {
  const site = siteId ? LAB_FIXTURE.sites.find((candidate) => candidate.id === siteId) : undefined;
  if (siteId && !site) return { points: [], series: [] };

  const cages = site ? cagesForSite(site.id) : LAB_FIXTURE.cages;
  const points = Array.from({ length: WEEK_COUNT }, (_unused, index) => `W${index + 1}`);
  const data = points.map((_label, index) => {
    const week = index + 1;
    return Math.round(cages.reduce((total, cage) => total + (cageWeekAt(cage.id, week)?.biomassKg ?? 0), 0));
  });

  return { points, series: [{ id: siteId ?? 'all-sites', label: seriesLabel, data }] };
};

// One point per site: average final weight against cumulative FCR. A single
// series keeps every point the same colour, since the six-series rule (spec
// 5.4) only governs categorical colour, not point count. `seriesLabel` is
// supplied by the caller rather than hardcoded.
export const deriveOverviewCorrelationData = (seriesLabel: string): ScatterSeriesData[] => {
  const points = LAB_FIXTURE.sites.map((site) => {
    const cages = cagesForSite(site.id);
    let totalWeight = 0;
    let totalFeed = 0;
    let totalGain = 0;
    let cageCount = 0;

    cages.forEach((cage) => {
      const weeks = cageWeeksFor(cage.id);
      const first = weeks[0];
      const last = weeks[weeks.length - 1];
      if (!first || !last) return;

      totalWeight += last.averageWeightG;
      totalFeed += weeks.reduce((total, cageWeek) => total + cageWeek.feedKg, 0);
      totalGain += last.biomassKg - first.biomassKg;
      cageCount += 1;
    });

    const averageWeight = cageCount > 0 ? totalWeight / cageCount : 0;
    const fcr = totalGain > 0 ? totalFeed / totalGain : 0;

    return { id: site.id, x: Math.round(averageWeight), y: Math.round(fcr * 100) / 100 };
  });

  return [{ id: 'sites', label: seriesLabel, points }];
};

// --- Drill-down ---------------------------------------------------------
//
// The site donut reuses the Overview groups, followed by cage bars and a
// fish-group table.

// Cages within one site, biomass at the latest week. Returns empty arrays when
// `siteId` matches no real site (the site donut's "Other" bucket has no
// backing cages), so the screen can show an empty state instead of an empty
// bar chart.
export const deriveCagesInSite = (siteId: string): CartesianData => {
  const site = LAB_FIXTURE.sites.find((candidate) => candidate.id === siteId);
  if (!site) return { categories: [], series: [] };

  const categories: CategoryPoint[] = cagesForSite(site.id).map((cage) => ({
    id: cage.id,
    label: cage.name,
    value: Math.round(cageWeekAt(cage.id, LATEST_WEEK)?.biomassKg ?? 0),
  }));

  return { categories, series: [{ id: 'biomass', label: 'Biomass (kg)', data: categories.map((category) => category.value) }] };
};

export interface FishGroupCageRow {
  id: string;
  name: string;
  biomassKg: number;
  stockedWeek: number;
}

// A fish group's "current" cage is the destination of its latest transfer;
// `Transfer` carries no separate "still here" flag, so the latest by week
// stands in for the group's present location.
export const deriveFishGroupsInCage = (cageId: string): FishGroupCageRow[] =>
  LAB_FIXTURE.fishGroups
    .map((group) => {
      const latestTransfer = [...group.transfers].sort((a, b) => b.week - a.week)[0];
      if (!latestTransfer || latestTransfer.toCageId !== cageId) return null;
      return { id: group.id, name: group.name, biomassKg: latestTransfer.biomassKg, stockedWeek: group.transfers[0].week };
    })
    .filter((row): row is FishGroupCageRow => row !== null)
    .sort((a, b) => b.biomassKg - a.biomassKg);

// --- Flow ----------------------------------------------------------------

export interface FlowSmallMultiple {
  cageId: string;
  siteId: string;
  label: string;
  series: number[];
  endValue: number;
  absoluteChange: number;
}

// Sort cage population sparklines by absolute change so the largest changes
// appear first.
export const deriveFlowSmallMultiples = (): FlowSmallMultiple[] =>
  LAB_FIXTURE.cages
    .map((cage) => {
      const series = populationSeriesFor(cage.id);
      const endValue = series[series.length - 1] ?? 0;
      const absoluteChange = series.length > 0 ? Math.abs(endValue - series[0]) : 0;
      return { cageId: cage.id, siteId: cage.siteId, label: `${cage.siteId} ${cage.name}`, series, endValue, absoluteChange };
    })
    .sort((a, b) => b.absoluteChange - a.absoluteChange);

export interface TransferSummaryRow {
  id: string;
  fishGroupId: string;
  fishGroupName: string;
  originCageId: string | null;
  originLabel: string;
  destinationCageId: string;
  destinationLabel: string;
  week: number;
  biomassKg: number;
}

const cageLabelFor = (cageId: string): string => {
  const cage = LAB_FIXTURE.cages.find((candidate) => candidate.id === cageId);
  return cage ? `${cage.siteId} ${cage.name}` : cageId;
};

// Flattens every fish group's transfer history. The stocking transfer
// (`fromCageId: null`) is kept, with `stockingLabel` standing in for its
// origin, rather than filtered out, since it is a real, dated movement of fish
// into the system. `stockingLabel` is a caller-supplied, pre-formatted string
// rather than a literal so this stays a plain data-derivation function with no
// i18n dependency of its own, consistent with every other function here.
export const deriveTransferSummary = (stockingLabel: string): TransferSummaryRow[] =>
  LAB_FIXTURE.fishGroups
    .flatMap((group) =>
      group.transfers.map((transfer, index) => ({
        id: `${group.id}-${index}`,
        fishGroupId: group.id,
        fishGroupName: group.name,
        originCageId: transfer.fromCageId,
        originLabel: transfer.fromCageId ? cageLabelFor(transfer.fromCageId) : stockingLabel,
        destinationCageId: transfer.toCageId,
        destinationLabel: cageLabelFor(transfer.toCageId),
        week: transfer.week,
        biomassKg: transfer.biomassKg,
      }))
    )
    .sort((a, b) => a.week - b.week);

// --- Embedded analytics --------------------------------------------------

export interface WeightClassSegment {
  label: string;
  value: number;
}

export interface EmbeddedGridRow {
  id: string;
  cageName: string;
  siteId: string;
  trend: number[];
  utilizationPercent: number;
  health: HealthStatus;
  distribution: WeightClassSegment[];
  deltaPercent: number;
}

const WEIGHT_CLASS_LABELS = ['<150g', '150–300g', '300–600g', '600g–1kg', '>1kg'];
const WEIGHT_CLASS_MIDPOINTS = [100, 225, 450, 800, 1500];

// No fixture field carries a weight-class breakdown, so this synthesises one
// deterministically from `averageWeightG`: a triangular kernel centred on
// whichever of the five bands the cage's current average weight falls
// nearest, read biologically as "most fish cluster near the average, a few
// spread to neighbouring classes." No `Math.random()` here; the only input is
// the fixture's own (seeded) average weight, so the result never drifts
// between loads.
const deriveWeightClassDistribution = (averageWeightG: number): WeightClassSegment[] => {
  const centerIndex = WEIGHT_CLASS_MIDPOINTS.reduce(
    (closest, midpoint, index) =>
      Math.abs(midpoint - averageWeightG) < Math.abs(WEIGHT_CLASS_MIDPOINTS[closest] - averageWeightG) ? index : closest,
    0
  );

  return WEIGHT_CLASS_LABELS.map((label, index) => {
    const distance = Math.abs(index - centerIndex);
    const value = distance === 0 ? 44 : distance === 1 ? 22 : 6;
    return { label, value };
  });
};

// `deltaPercent` compares the latest biomass with the start of the same recent
// window used by the Overview KPIs.
export const deriveEmbeddedGridRows = (): EmbeddedGridRow[] =>
  LAB_FIXTURE.cages.map((cage) => {
    const weeks = cageWeeksFor(cage.id);
    const first = weeks[0];
    const last = weeks[weeks.length - 1];
    const recentFirst = weeks[Math.max(0, weeks.length - RECENT_WEEK_COUNT)];

    const cumulativeMortality = weeks.reduce((total, cageWeek) => total + cageWeek.mortality, 0);
    const initialPopulation = first ? Math.round(first.biomassKg / (first.averageWeightG / 1000)) + first.mortality : 0;
    const mortalityRatePercent = initialPopulation > 0 ? (cumulativeMortality / initialPopulation) * 100 : 0;

    const utilizationPercent = last ? Math.min(100, (last.biomassKg / cage.capacityKg) * 100) : 0;
    const deltaPercent =
      recentFirst && last && recentFirst.biomassKg > 0 ? ((last.biomassKg - recentFirst.biomassKg) / recentFirst.biomassKg) * 100 : 0;

    return {
      id: cage.id,
      cageName: `${cage.siteId} ${cage.name}`,
      siteId: cage.siteId,
      trend: weeks.map((cageWeek) => cageWeek.biomassKg),
      utilizationPercent: Math.round(utilizationPercent * 10) / 10,
      health: healthStatusFor(mortalityRatePercent, MORTALITY_THRESHOLDS),
      distribution: deriveWeightClassDistribution(last?.averageWeightG ?? 0),
      deltaPercent: Math.round(deltaPercent * 10) / 10,
    };
  });
